import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";

import GrecoReactGrid, {
  standardCellTypeHandlers,
} from "components/grecoreactgrid/GrecoReactGrid";
import { importedInvoiceEntriesColumnConfig } from "./importedInvoiceEntriesColumnConfig";
import ImportedInvoiceEntriesSearchBar from "./ImportedInvoiceEntriesSearchBar";
import SortableHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SortableHeaderCellTypeHandler";
import SumHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SumHeaderCellTypeHandler";
import CustomTextCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomTextCellTypeHandler";
import CustomNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomNumberCellTypeHandler";
import OrdinalNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/OrdinalNumberCellTypeHandler";
import CustomCheckmarkCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomCheckmarkCellTypeHandler";
import { useTheme } from "@emotion/react";

// Detail pane of the master/detail page: the member offers of the selected invoice
// with the installment each one was matched to (orange rows are matched). Read only:
// matching is automatic (import / re-match), there is no manual match.
const ImportedInvoiceEntryGrid = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { taxonomy, grecoCompanies } = useAppStoreState();
  const importedInvoicesStore = useImportedInvoicesStore();
  const [
    {
      importedInvoiceEntriesSort,
      importedInvoiceEntryMetadataMap,
      importedInvoiceEntries,
      entriesSearchTerm,
    },
    {
      setImportedInvoicesStoreValues,
      setImportedInvoicesStoreValue,
      filterImportedInvoiceEntries,
    },
  ] = importedInvoicesStore;

  useEffect(() => {
    filterImportedInvoiceEntries({ entriesSearchTerm });
  }, [importedInvoiceEntries, entriesSearchTerm]);

  const handleChanges = (
    updatedRows: any[],
    updatedRowsMetadataMap: { [key: number]: any }
  ) => {
    setImportedInvoicesStoreValues({
      importedInvoiceEntries: importedInvoiceEntries.map((entry) => {
        const updatedEntry = updatedRows.find(
          (row) => row.importedInvoiceEntryId === entry.importedInvoiceEntryId
        );
        return updatedEntry ? updatedEntry : entry;
      }),
      importedInvoiceEntryMetadataMap: updatedRowsMetadataMap,
    });
  };

  const cellTypeHandlers = {
    ...standardCellTypeHandlers,
    sortableheader: new SortableHeaderCellTypeHandler(),
    sumheader: new SumHeaderCellTypeHandler(),
    customtext: new CustomTextCellTypeHandler(),
    customnumber: new CustomNumberCellTypeHandler(),
    ordinalnumber: new OrdinalNumberCellTypeHandler(),
    customcheckmark: new CustomCheckmarkCellTypeHandler(),
  };

  return (
    <div style={{ height: "100%", overflow: "auto" }}>
      <ImportedInvoiceEntriesSearchBar />
      <div
        data-help="imported-invoices-entries-grid"
        style={{ height: "calc(100% - 60px)", overflow: "scroll" }}
      >
        <GrecoReactGrid
          rows={importedInvoiceEntries}
          columns={importedInvoiceEntriesColumnConfig}
          idColumn={"importedInvoiceEntryId"}
          rowsMetadataMap={importedInvoiceEntryMetadataMap}
          handleChanges={handleChanges}
          cellTypeHandlers={cellTypeHandlers}
          hasSumHeaderRow={true}
          hasSelectColumn={false}
          keyColumn="importedInvoiceEntryId"
          stickyColumns={{
            left: 2,
            right: 0,
            top: 1,
            bottom: 1,
          }}
          context={{
            taxonomy: taxonomy,
            t: t,
            importedInvoicesStore: importedInvoicesStore,
            theme: theme,
            grecoCompanies: grecoCompanies,
            sort: importedInvoiceEntriesSort,
            setSort: (sort: any) => {
              setImportedInvoicesStoreValue("importedInvoiceEntriesSort", sort);
            },
          }}
          sort={importedInvoiceEntriesSort}
        />
      </div>
    </div>
  );
};

export default ImportedInvoiceEntryGrid;
